/**
 * Control Bridge Domain Selector (post-workshop)
 * Shown when user selects YES at Readiness Gate. User selects a domain, pays commitment toll, then navigates to domain-specific flow.
 * @see docs/business_objective_workshop.md
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, Button, message, Spin, Alert, Typography, InputNumber } from 'antd';
import {
  ArrowLeftOutlined,
  TeamOutlined,
  DollarOutlined,
  CustomerServiceOutlined,
  AppstoreOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  getWorkshopObjective,
  determineWorkshopRoute,
  commitDomainFromControlBridge,
  updateBridgeSession,
  type WorkshopObjectiveResponse,
} from '../services/localBridgeSessionApi';

const { Text } = Typography;

type DomainId = 'business' | 'stakeholder' | 'product' | 'customer';

/** Map primary_route from API to domain id */
const PRIMARY_ROUTE_TO_DOMAIN: Record<string, DomainId> = {
  business_domain: 'business',
  stakeholder_domain: 'stakeholder',
  product_domain: 'product',
};

const DOMAINS: Array<{
  id: DomainId;
  name: string;
  description: string;
  icon: React.ReactNode;
  route: string | null;
}> = [
  {
    id: 'business',
    name: 'Business',
    description: 'Bridge Session: questions, entropy heat map, Bridge Card',
    icon: <DollarOutlined />,
    route: '/bridge-session',
  },
  {
    id: 'stakeholder',
    name: 'Stakeholder',
    description: 'Coming soon',
    icon: <TeamOutlined />,
    route: null,
  },
  {
    id: 'product',
    name: 'Product',
    description: 'Product Domain Bridge: questions, entropy heat map, Bridge Card',
    icon: <AppstoreOutlined />,
    route: '/local-bridge/product-domain-bridge',
  },
  {
    id: 'customer',
    name: 'Customer',
    description: 'MarketFit guided flow: product → situation → actor',
    icon: <CustomerServiceOutlined />,
    route: '/local-bridge/marketfit/guided',
  },
];

export const ControlBridgeDomainSelectorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();

  const [data, setData] = useState<WorkshopObjectiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DomainId | null>(null);
  const [commitmentToll, setCommitmentToll] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (!sessionId || !isLocalBridgeApiEnabled()) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const d = await getWorkshopObjective(sessionId);
      setData(d ?? null);
      if (d?.objective?.commitment_toll != null) {
        setCommitmentToll(Number(d.objective.commitment_toll));
      } else if (d?.objective?.value_per_month != null) {
        setCommitmentToll(Math.round(Number(d.objective.value_per_month) / 160));
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pre-select recommended domain when we have primary_route
  useEffect(() => {
    if (!data?.objective?.primary_route || selectedDomain) return;
    const recommended = PRIMARY_ROUTE_TO_DOMAIN[data.objective.primary_route];
    if (recommended) {
      setSelectedDomain(recommended);
    }
  }, [data?.objective?.primary_route]);

  // Determine route on load if not yet computed; then refresh (which triggers pre-select above)
  const hasDeterminedRoute = React.useRef(false);
  useEffect(() => {
    if (!sessionId || !data?.objective || data.objective.primary_route || hasDeterminedRoute.current) return;
    hasDeterminedRoute.current = true;
    let cancelled = false;
    const run = async () => {
      const result = await determineWorkshopRoute(sessionId);
      if (cancelled || !result) return;
      const refreshed = await getWorkshopObjective(sessionId);
      if (cancelled || !refreshed) return;
      setData(refreshed);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, data?.objective?.primary_route, data?.objective?.id]);

  const handleDomainSelect = (domainId: DomainId) => {
    setSelectedDomain(domainId);
    if (data?.objective?.commitment_toll != null && commitmentToll == null) {
      setCommitmentToll(Number(data.objective.commitment_toll));
    } else if (data?.objective?.value_per_month != null && commitmentToll == null) {
      setCommitmentToll(Math.round(Number(data.objective.value_per_month) / 160));
    }
  };

  const handleCommit = async (tollStatus: 'ACCEPTED' | 'DEFERRED' | 'DECLINED') => {
    if (!sessionId || !selectedDomain) return;
    const domain = DOMAINS.find((d) => d.id === selectedDomain);
    if (!domain) return;

    setSubmitting(true);
    try {
      const toll = commitmentToll ?? data?.objective?.commitment_toll ?? data?.objective?.value_per_month
        ? Math.round(Number(data?.objective?.value_per_month) / 160)
        : 0;

      const updated = await commitDomainFromControlBridge(sessionId, {
        selected_bridge_domain: selectedDomain,
        commitment_toll: toll,
        toll_status: tollStatus,
      });

      if (!updated) {
        message.error('Failed to save');
        return;
      }

      if (tollStatus === 'ACCEPTED') {
        await updateBridgeSession(sessionId, { status: 'collecting_current', domain: selectedDomain });
        message.success('Commitment accepted. Starting domain flow.');

        if (domain.route) {
          if (domain.id === 'business') {
            navigate(`/bridge-session/${sessionId}?step=questions`);
          } else if (domain.id === 'customer') {
            navigate(`/local-bridge/marketfit/guided?sessionId=${sessionId}`);
          } else if (domain.id === 'product') {
            const productDomainSessionId = updated?.product_domain_session_id;
            if (productDomainSessionId) {
              navigate(`/local-bridge/product-domain-bridge/${productDomainSessionId}?sessionId=${sessionId}&step=product_context`);
            } else {
              message.warning('Product Domain session could not be created. Please try again.');
            }
          } else {
            navigate(domain.route + `?sessionId=${sessionId}`);
          }
        } else {
          message.info('This domain flow is coming soon.');
          setSelectedDomain(null);
        }
      } else {
        message.info(tollStatus === 'DEFERRED' ? 'Deferred.' : 'Declined.');
        setSelectedDomain(null);
      }
    } catch {
      message.error('Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLocalBridgeApiEnabled()) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="Local Bridge API not configured" />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" message="No session" description="Session ID is required." />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Spin tip="Loading..." />
      </div>
    );
  }

  const valuePerMonth = data?.objective?.value_per_month;
  const defaultToll = valuePerMonth != null ? Math.round(Number(valuePerMonth) / 160) : 0;
  const primaryRoute = data?.objective?.primary_route;
  const routingReason = data?.objective?.routing_reason;
  const recommendedDomain = primaryRoute ? PRIMARY_ROUTE_TO_DOMAIN[primaryRoute] : null;
  const recommendedDomainName = recommendedDomain ? DOMAINS.find((d) => d.id === recommendedDomain)?.name ?? recommendedDomain : null;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <Link to="/local-bridge">
        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }}>
          Back to Local Bridge
        </Button>
      </Link>

      <Typography.Title level={4}>Select a domain to continue</Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Each domain has its own Bridge Session flow. Select one and commit to the toll to begin.
      </Text>

      {primaryRoute && recommendedDomainName && (
        <Alert
          type="info"
          message={
            <>
              Based on your objective and primary constraint, the most leveraged next step is: <strong>{recommendedDomainName}</strong>.
            </>
          }
          description={routingReason}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {DOMAINS.map((domain) => (
          <Card
            key={domain.id}
            size="small"
            style={{
              borderColor: selectedDomain === domain.id ? '#1890ff' : undefined,
              cursor: 'pointer',
            }}
            onClick={() => handleDomainSelect(domain.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ fontSize: 24, color: '#1890ff' }}>{domain.icon}</div>
              <div style={{ flex: 1 }}>
                <span>
                  <Text strong>{domain.name}</Text>
                  {recommendedDomain === domain.id && (
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      (Recommended)
                    </Text>
                  )}
                </span>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {domain.description}
                </Text>
              </div>
            </div>

            {selectedDomain === domain.id && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Commitment Toll (1 hr value)
                </Text>
                <InputNumber
                  value={commitmentToll ?? defaultToll}
                  onChange={(v) => setCommitmentToll(v ?? null)}
                  min={0}
                  style={{ width: 120, marginBottom: 12 }}
                  prefix="$"
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCommit('ACCEPTED');
                    }}
                    loading={submitting}
                  >
                    Accept
                  </Button>
                  <Button
                    icon={<ClockCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCommit('DEFERRED');
                    }}
                    loading={submitting}
                  >
                    Defer
                  </Button>
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCommit('DECLINED');
                    }}
                    loading={submitting}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};
